import React, { useEffect } from 'react';
import { useForm, Controller } from 'react-hook-form';
import {
  Box,
  Button,
  TextField,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import { IssueFormData, IssueStatus } from '../types/issue';

interface IssueFormProps {
  open: boolean;
  onClose: () => void;
  initialData?: IssueFormData;
  onSubmit: (data: IssueFormData) => void;
  mode: 'add' | 'edit';
  isLoading?: boolean;
}

const defaultValues: IssueFormData = {
  title: '',
  description: '',
  volume: new Date().getFullYear(),
  number: 1,
  year: new Date().getFullYear(),
  status: IssueStatus.DRAFT,
  topics: [],
};

export const IssueForm: React.FC<IssueFormProps> = ({
  open,
  onClose,
  initialData,
  onSubmit,
  mode,
  isLoading = false,
}) => {
  const { control, handleSubmit, formState: { errors }, reset } = useForm<IssueFormData>({
    defaultValues: defaultValues,
  });

  useEffect(() => {
    if (mode === 'edit' && initialData) {
      reset(initialData);
    } else {
      reset(defaultValues);
    }
  }, [mode, initialData, open, reset]);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        {mode === 'add' ? 'Добавить выпуск' : 'Редактировать выпуск'}
      </DialogTitle>
      <Box component="form" onSubmit={handleSubmit(onSubmit)} noValidate>
        <DialogContent>
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <Controller
                name="title"
                control={control}
                rules={{ required: 'Название обязательно' }}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="Название выпуска"
                    fullWidth
                    error={!!errors.title}
                    helperText={errors.title?.message}
                  />
                )}
              />
            </Grid>

            <Grid item xs={12}>
              <Controller
                name="description"
                control={control}
                rules={{ required: 'Описание обязательно' }}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="Описание"
                    multiline
                    rows={4}
                    fullWidth
                    error={!!errors.description}
                    helperText={errors.description?.message}
                  />
                )}
              />
            </Grid>

            <Grid item xs={12} sm={4}>
              <Controller
                name="volume"
                control={control}
                rules={{ required: 'Том обязателен' }}
                render={({ field }) => (
                  <TextField
                    {...field}
                    type="number"
                    label="Том"
                    fullWidth
                    error={!!errors.volume}
                    helperText={errors.volume?.message}
                  />
                )}
              />
            </Grid>

            <Grid item xs={12} sm={4}>
              <Controller
                name="number"
                control={control}
                rules={{ required: 'Номер обязателен' }}
                render={({ field }) => (
                  <TextField
                    {...field}
                    type="number"
                    label="Номер"
                    fullWidth
                    error={!!errors.number}
                    helperText={errors.number?.message}
                  />
                )}
              />
            </Grid>

            <Grid item xs={12} sm={4}>
              <Controller
                name="year"
                control={control}
                rules={{ required: 'Год обязателен' }}
                render={({ field }) => (
                  <TextField
                    {...field}
                    type="number"
                    label="Год"
                    fullWidth
                    error={!!errors.year}
                    helperText={errors.year?.message}
                  />
                )}
              />
            </Grid>

            <Grid item xs={12}>
              <Controller
                name="status"
                control={control}
                rules={{ required: 'Статус обязателен' }}
                render={({ field }) => (
                  <FormControl fullWidth error={!!errors.status}>
                    <InputLabel>Статус</InputLabel>
                    <Select {...field} label="Статус">
                      <MenuItem value={IssueStatus.DRAFT}>Черновик</MenuItem>
                      <MenuItem value={IssueStatus.PUBLISHED}>Опубликован</MenuItem>
                      <MenuItem value={IssueStatus.ARCHIVED}>Архивирован</MenuItem>
                    </Select>
                    {errors.status && (
                      <Typography variant="caption" color="error">
                        {errors.status.message}
                      </Typography>
                    )}
                  </FormControl>
                )}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>Отмена</Button>
          <Button type="submit" variant="contained" color="primary" disabled={isLoading}>
            {mode === 'add' ? 'Добавить' : 'Сохранить'}
          </Button>
        </DialogActions>
      </Box>
    </Dialog>
  );
}; 